import React, { useState } from 'react';
import { testEmailSending, testSingleEmail, testEmailConfig } from '../utils/emailTest';

const EmailTest: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [testResults, setTestResults] = useState<string[]>([]);
  const [customEmail, setCustomEmail] = useState('');

  const addLog = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const clearLogs = () => {
    setTestResults([]);
  };

  const handleTestAllEmails = async () => {
    setIsLoading(true);
    clearLogs();
    
    addLog('🧪 Starting comprehensive email test...');
    
    // Capture console logs
    const originalLog = console.log;
    console.log = (...args) => {
      addLog(args.join(' '));
      originalLog(...args);
    };
    
    try {
      await testEmailSending();
      addLog('✅ Email test completed successfully!');
    } catch (error) {
      addLog(`❌ Email test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      console.log = originalLog;
      setIsLoading(false);
    }
  };

  const handleTestSingleEmail = async () => {
    if (!customEmail.trim()) {
      addLog('❌ Please enter an email address');
      return;
    }

    setIsLoading(true);
    
    addLog(`🧪 Testing single email: ${customEmail}`);
    
    try {
      const success = await testSingleEmail(customEmail);
      if (success) {
        addLog(`✅ Email sent successfully to ${customEmail}`);
      } else {
        addLog(`❌ Failed to send email to ${customEmail}`);
      }
    } catch (error) {
      addLog(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConfig = () => {
    clearLogs();
    addLog('🔧 Testing email configuration...');
    
    // Capture console logs
    const originalLog = console.log;
    console.log = (...args) => {
      addLog(args.join(' '));
      originalLog(...args);
    };
    
    try {
      testEmailConfig();
    } catch (error) {
      addLog(`❌ Configuration test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      console.log = originalLog;
    }
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Poppins, sans-serif' }}>
      <h1 style={{ color: 'var(--color-heading)', marginBottom: '30px' }}>
        📧 Email Testing Dashboard
      </h1>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
        {/* Test Controls */}
        <div style={{ backgroundColor: '#f8f9fa', padding: '30px', borderRadius: '8px' }}>
          <h2 style={{ color: 'var(--color-heading)', marginBottom: '20px' }}>Test Controls</h2>
          
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={handleTestAllEmails}
              disabled={isLoading}
              style={{
                backgroundColor: 'var(--color-green)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                marginRight: '10px',
                marginBottom: '10px',
                display: 'block',
                width: '100%'
              }}
            >
              {isLoading ? '⏳ Testing...' : '🧪 Test All Emails (ethienne.s@itpac.co.za & ethiennesmit@icloud.com)'}
            </button>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <input
              type="email"
              placeholder="Enter custom email address"
              value={customEmail}
              onChange={(e) => setCustomEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '2px solid var(--color-light-grey)',
                marginBottom: '10px',
                fontFamily: 'Poppins, sans-serif'
              }}
            />
            <button
              onClick={handleTestSingleEmail}
              disabled={isLoading || !customEmail.trim()}
              style={{
                backgroundColor: 'var(--color-background)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: (isLoading || !customEmail.trim()) ? 'not-allowed' : 'pointer',
                opacity: (isLoading || !customEmail.trim()) ? 0.6 : 1,
                width: '100%'
              }}
            >
              📧 Test Single Email
            </button>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={handleTestConfig}
              disabled={isLoading}
              style={{
                backgroundColor: 'var(--color-pink)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                width: '100%'
              }}
            >
              🔧 Test Configuration
            </button>
          </div>

          <button
            onClick={clearLogs}
            style={{
              backgroundColor: 'var(--color-grey)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              fontSize: '14px',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            🗑️ Clear Logs
          </button>
        </div>

        {/* Configuration Info */}
        <div style={{ backgroundColor: '#f8f9fa', padding: '30px', borderRadius: '8px' }}>
          <h2 style={{ color: 'var(--color-heading)', marginBottom: '20px' }}>Configuration Info</h2>
          
          <div style={{ fontSize: '14px', color: 'var(--color-grey)', lineHeight: '1.6' }}>
            <p><strong>Test Emails:</strong></p>
            <ul>
              <li>ethienne.s@itpac.co.za</li>
              <li>ethiennesmit@icloud.com</li>
            </ul>
            
            <p><strong>SMTP Provider:</strong> ITPAC (Primary) / SMTP2GO (Backup)</p>
            <p><strong>Server:</strong> mail.itpac.tech:465 (SSL)</p>
            <p><strong>From:</strong> noreply@itpac.tech</p>
            
            <p><strong>Test Data:</strong></p>
            <ul>
              <li>Name: Test User</li>
              <li>Amount: R150</li>
              <li>Type: One-time donation</li>
              <li>Transaction ID: Auto-generated</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Test Results */}
      <div style={{ backgroundColor: '#f8f9fa', padding: '30px', borderRadius: '8px' }}>
        <h2 style={{ color: 'var(--color-heading)', marginBottom: '20px' }}>
          Test Results {testResults.length > 0 && `(${testResults.length} entries)`}
        </h2>
        
        <div 
          style={{
            backgroundColor: '#000',
            color: '#00ff00',
            padding: '20px',
            fontFamily: 'monospace',
            fontSize: '14px',
            lineHeight: '1.4',
            maxHeight: '400px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            border: '2px solid var(--color-light-grey)'
          }}
        >
          {testResults.length === 0 ? (
            <div style={{ color: '#888' }}>No test results yet. Click a test button to begin...</div>
          ) : (
            testResults.map((result, index) => (
              <div key={index}>{result}</div>
            ))
          )}
        </div>
      </div>

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px' }}>
        <h3 style={{ color: '#856404', marginBottom: '10px' }}>⚠️ Important Notes</h3>
        <ul style={{ color: '#856404', fontSize: '14px', lineHeight: '1.6' }}>
          <li><strong>Browser Limitation:</strong> Browsers cannot send real emails directly due to security restrictions</li>
          <li><strong>Current Test:</strong> Validates configuration and simulates email sending process</li>
          <li><strong>Real Email Testing:</strong> Use the Node.js script: <code>npm run test:email</code></li>
          <li><strong>Production:</strong> Requires server-side implementation or SMTP2GO API key</li>
          <li><strong>Validation:</strong> This test ensures all settings and templates are correct</li>
        </ul>
      </div>

      <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '8px' }}>
        <h3 style={{ color: '#0c5460', marginBottom: '10px' }}>🔧 For Real Email Testing</h3>
        <div style={{ color: '#0c5460', fontSize: '14px', lineHeight: '1.6' }}>
          <p><strong>Option 1: Command Line (Recommended)</strong></p>
          <code style={{ backgroundColor: '#f8f9fa', padding: '4px 8px', borderRadius: '4px' }}>npm run test:email</code>
          
          <p style={{ marginTop: '15px' }}><strong>Option 2: Get SMTP2GO API Key</strong></p>
          <ol style={{ marginLeft: '20px' }}>
            <li>Login to SMTP2GO dashboard</li>
            <li>Generate API key</li>
            <li>Add to config.js: <code>apiKey: 'your_key'</code></li>
          </ol>
          
          <p style={{ marginTop: '15px' }}><strong>Option 3: Server-Side Implementation</strong></p>
          <p>Create backend endpoint using nodemailer with your SMTP credentials</p>
        </div>
      </div>
    </div>
  );
};

export default EmailTest;
